import { Github, Linkedin, Mail, Menu, Rss, X, type LucideIcon } from "lucide-react";
import { useState } from "react";
import { isExternal, type SiteSettings, type SocialIcon } from "../types.ts";

export const socialIcons: Record<SocialIcon, LucideIcon> = {
  github: Github,
  linkedin: Linkedin,
  mail: Mail,
  rss: Rss,
};

/** External links open in a new tab. */
export const linkProps = (href: string) =>
  isExternal(href) ? { href, target: "_blank", rel: "noopener noreferrer" } : { href };

export function SiteHeader({ site }: { site: SiteSettings }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { navLinks } = site.header;

  return (
    <header className="fixed top-0 z-50 w-full border-b border-border bg-background/80 backdrop-blur-xl print:hidden">
      <div className="mx-auto flex h-14 max-w-3xl items-center justify-between px-6">
        <a href="/" aria-label={site.seo.siteName} className="flex items-center">
          <img src="/shams-dev-dark.svg" alt="shams.dev" className="h-5 w-auto" />
        </a>

        <nav className="hidden items-center gap-6 md:flex" aria-label="Main">
          {navLinks.map((link) => (
            <a
              key={link.href}
              {...linkProps(link.href)}
              className="text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              {link.label}
            </a>
          ))}
        </nav>

        <button
          className="md:hidden"
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          {mobileOpen ? (
            <X className="h-5 w-5 text-foreground" />
          ) : (
            <Menu className="h-5 w-5 text-foreground" />
          )}
        </button>
      </div>

      {mobileOpen && (
        <div className="border-t border-border bg-background/95 backdrop-blur-xl md:hidden">
          <div className="flex flex-col gap-4 px-6 py-6">
            {navLinks.map((link) => (
              <a
                key={link.href}
                {...linkProps(link.href)}
                onClick={() => setMobileOpen(false)}
                className="text-sm text-muted-foreground transition-colors hover:text-foreground"
              >
                {link.label}
              </a>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}

export function SiteFooter({
  site,
  year,
  feedPath,
}: {
  site: SiteSettings;
  year: number;
  feedPath: string;
}) {
  const { email, links } = site.social;

  return (
    <footer className="border-t border-border py-8 print:hidden">
      <div className="mx-auto flex max-w-3xl items-center justify-between px-6">
        <div className="flex flex-col items-start gap-2.5">
          <img src="/shams-dev-dark.svg" alt="shams.dev" className="block h-3 w-auto opacity-70" />
          {site.footer.copyright && (
            <p className="text-xs leading-none text-muted-foreground">
              &copy; {year} {site.footer.copyright}
            </p>
          )}
        </div>
        <div className="flex items-center gap-4">
          <a
            href={feedPath}
            className="text-xs text-muted-foreground transition-colors hover:text-foreground"
          >
            RSS
          </a>
          {links.map((link) => {
            const Icon = socialIcons[link.icon];
            return (
              <a
                key={link.href}
                {...linkProps(link.href)}
                aria-label={link.label}
                className="text-muted-foreground transition-colors hover:text-foreground"
              >
                <Icon className="h-4 w-4" />
              </a>
            );
          })}
          {email && (
            <a
              href={`mailto:${email}`}
              aria-label="Email"
              className="text-muted-foreground transition-colors hover:text-foreground"
            >
              <Mail className="h-4 w-4" />
            </a>
          )}
        </div>
      </div>
    </footer>
  );
}
